import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Transition } from '@headlessui/react';
import { CheckIcon, ChevronRightIcon, CircleStackIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import * as React from 'react';
import { type HTMLAttributes, type ReactElement, type ReactNode } from 'react';

interface ClonableTriggerProps extends HTMLAttributes<HTMLButtonElement> {
    ref?: React.Ref<HTMLElement>;
    'data-state'?: 'open' | 'closed';
}

interface ClonableItemProps {
    ref?: React.Ref<HTMLElement>;
    className?: string;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface DropdownMenuProps {
    children?: ReactNode;
}

const DropdownMenu = ({ children }: DropdownMenuProps) => {
    return (
        <Menu as='div' className='relative inline-block text-left'>
            {children}
        </Menu>
    );
};

interface DropdownMenuTriggerProps extends HTMLAttributes<HTMLButtonElement> {
    asChild?: boolean;
}

const DropdownMenuTrigger = React.forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>(
    ({ children, asChild, className, ...props }, ref) => {
        if (asChild && React.isValidElement(children)) {
            const child = children as ReactElement<ClonableTriggerProps>;

            return (
                <MenuButton as={ React.Fragment }>
                    {({ open }: { open: boolean }) => {

                        return React.cloneElement(child, {
                            ref,
                            ...props,
                            'data-state': open ? 'open' : 'closed',
                        });
                    }
                    }
                </MenuButton>
            );
        }

        return (
            <MenuButton className={ className } ref={ ref } { ...props }>
                {children}
            </MenuButton>
        );
    }
);

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

interface DropdownMenuContentProps extends HTMLAttributes<HTMLDivElement> {
    sideOffset?: number;
    align?: 'start' | 'center' | 'end';
}

const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
    ({ className, sideOffset = 4, align = 'end', children, ...props }, ref) => {
        const alignmentClasses = {
            start: 'left-0',
            center: 'left-1/2 -translate-x-1/2',
            end: 'right-0',
        };

        return (
            <Transition
                as={ React.Fragment }
                enter='transition ease-out duration-100'
                enterFrom='transform opacity-0 scale-95'
                enterTo='transform opacity-100 scale-100'
                leave='transition ease-in duration-75'
                leaveFrom='transform opacity-100 scale-100'
                leaveTo='transform opacity-0 scale-95'
            >
                <MenuItems
                    className={ clsx(
                        'absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md focus:outline-none',
                        alignmentClasses[align] || alignmentClasses.end,
                        className
                    ) }
                    ref={ ref }
                    style={ { marginTop: `${ sideOffset }px` } }
                    { ...props }
                >
                    {children}
                </MenuItems>
            </Transition>
        );
    }
);

DropdownMenuContent.displayName = 'DropdownMenuContent';

interface DropdownMenuItemProps extends HTMLAttributes<HTMLButtonElement> {
    inset?: boolean;
    onSelect?: () => void;
    disabled?: boolean;
    asChild?: boolean;
}

const DropdownMenuItem = React.forwardRef<HTMLButtonElement, DropdownMenuItemProps>(
    ({ className, inset, children, onSelect, disabled, asChild, ...props }, ref) => {
        return (
            <MenuItem disabled={ disabled }>
                {({ focus, disabled: isDisabled, close }: { focus: boolean; disabled: boolean; close: () => void }) => {
                    const itemClassName = clsx(
                        'relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
                        focus && 'bg-accent text-accent-foreground',
                        isDisabled && 'pointer-events-none opacity-50',
                        inset && 'pl-8',
                        className
                    );

                    if (asChild && React.isValidElement(children)) {
                        const child = children as ReactElement<ClonableItemProps>;

                        return React.cloneElement(child, {
                            ref,
                            className: clsx(itemClassName, child.props.className),
                            onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
                                child.props.onClick?.(e);
                                onSelect?.();
                                close();
                            },
                            ...props,
                        });
                    }

                    return (
                        <button
                            className={ itemClassName }
                            disabled={ isDisabled }
                            onClick={ () => {
                                onSelect?.();
                                close();
                            } }
                            ref={ ref }
                            type='button'
                            { ...props }
                        >
                            {children}
                        </button>
                    );
                }}
            </MenuItem>
        );
    }
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

interface DropdownMenuCheckboxItemProps extends HTMLAttributes<HTMLButtonElement> {
    checked?: boolean;
    onCheckedChange?: (checked: boolean) => void;
}

const DropdownMenuCheckboxItem = React.forwardRef<HTMLButtonElement, DropdownMenuCheckboxItemProps>(
    ({ className, children, checked, onCheckedChange, ...props }, ref) => {
        return (
            <MenuItem>
                {({ focus }: { focus: boolean }) => {
                    return (
                        <button
                            className={ clsx(
                                'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors',
                                focus && 'bg-accent text-accent-foreground',
                                className
                            ) }
                            onClick={ () => {
                                return onCheckedChange?.(!checked);
                            } }
                            ref={ ref }
                            type='button'
                            { ...props }
                        >
                            <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
                                {checked && <CheckIcon className='h-4 w-4' />}
                            </span>
                            {children}
                        </button>
                    );
                }}
            </MenuItem>
        );
    }
);

DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem';

interface DropdownMenuRadioGroupProps {
    children?: ReactNode;
    value?: string;
    onValueChange?: (value: string) => void;
}

interface ClonableRadioItemProps {
    groupValue?: string;
    onValueChange?: (value: string) => void;
}

const DropdownMenuRadioGroup = ({ children, value, onValueChange }: DropdownMenuRadioGroupProps) => {
    return (
        <div data-value={ value } role='group'>
            {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child as ReactElement<ClonableRadioItemProps>, { groupValue: value, onValueChange });
                }
                return child;
            })}
        </div>
    );
};

DropdownMenuRadioGroup.displayName = 'DropdownMenuRadioGroup';

interface DropdownMenuRadioItemProps extends HTMLAttributes<HTMLButtonElement> {
    value?: string;
    groupValue?: string;
    onValueChange?: (value: string) => void;
}

const DropdownMenuRadioItem = React.forwardRef<HTMLButtonElement, DropdownMenuRadioItemProps>(
    (
        { className, children, value, groupValue, onValueChange, ...props },
        ref
    ) => {
        return (
            <MenuItem>
                {({ focus }: { focus: boolean }) => {
                    return (
                        <button
                            className={ clsx(
                                'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors',
                                focus && 'bg-accent text-accent-foreground',
                                className
                            ) }
                            onClick={ () => {
                                return onValueChange?.(value ?? '');
                            } }
                            ref={ ref }
                            type='button'
                            { ...props }
                        >
                            <span className='absolute left-2 flex h-3.5 w-3.5 items-center justify-center'>
                                {groupValue === value && (
                                    <CircleStackIcon className='h-2 w-2 fill-current' />
                                )}
                            </span>
                            {children}
                        </button>
                    );
                }}
            </MenuItem>
        );
    }
);

DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem';

interface DropdownMenuLabelProps extends HTMLAttributes<HTMLDivElement> {
    inset?: boolean;
}

const DropdownMenuLabel = React.forwardRef<HTMLDivElement, DropdownMenuLabelProps>(
    ({ className, inset, ...props }, ref) => {
        return (
            <div
                className={ clsx(
                    'px-2 py-1.5 text-sm font-semibold',
                    inset && 'pl-8',
                    className
                ) }
                ref={ ref }
                { ...props }
            />
        );
    }
);

DropdownMenuLabel.displayName = 'DropdownMenuLabel';

const DropdownMenuSeparator = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        return (
            <div
                className={ clsx('-mx-1 my-1 h-px bg-muted', className) }
                ref={ ref }
                { ...props }
            />
        );
    }
);

DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';

const DropdownMenuShortcut = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => {
    return (
        <span
            className={ clsx('ml-auto text-xs tracking-widest opacity-60', className) }
            { ...props }
        />
    );
};

DropdownMenuShortcut.displayName = 'DropdownMenuShortcut';

// Compatibility stubs for sub-menus (Headless UI Menu doesn't support nested menus out of the box)
const DropdownMenuGroup = ({ children }: { children?: ReactNode }) => {
    return <div role='group'>{children}</div>;
};
const DropdownMenuPortal = ({ children }: { children?: ReactNode }) => {
    return children;
};
const DropdownMenuSub = ({ children }: { children?: ReactNode }) => {
    return <div>{children}</div>;
};
const DropdownMenuSubContent = React.forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => {
        return (
            <div
                className={ clsx(
                    'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-lg',
                    className
                ) }
                ref={ ref }
                { ...props }
            />
        );
    }
);

DropdownMenuSubContent.displayName = 'DropdownMenuSubContent';

interface DropdownMenuSubTriggerProps extends HTMLAttributes<HTMLButtonElement> {
    inset?: boolean;
}

const DropdownMenuSubTrigger = React.forwardRef<HTMLButtonElement, DropdownMenuSubTriggerProps>(
    ({ className, inset, children, ...props }, ref) => {
        return (
            <button
                className={ clsx(
                    'flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent',
                    inset && 'pl-8',
                    className
                ) }
                ref={ ref }
                type='button'
                { ...props }
            >
                {children}
                <ChevronRightIcon className='ml-auto h-4 w-4' />
            </button>
        );
    }
);

DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger';

export {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger };
